// importLocationCsvUtils.js

export const LOCATION_FIELD_TYPE = 'location';
export const LOCATION_PART_SEPARATOR = '::';
export const LOCATION_FULL_PART_ID = 'full';

export const LOCATION_ADDRESS_PARTS = [
  {
    id: 'line1',
    templateLabel: 'Address Line 1',
    prefixedLabel: 'Line 1',
    aliases: ['addressline1', 'address1', 'street', 'street1', 'streetaddress', 'addressline'],
    example: '5110 Central Ave',
  },
  {
    id: 'line2',
    templateLabel: 'Address Line 2',
    prefixedLabel: 'Line 2',
    aliases: ['addressline2', 'address2', 'apt', 'suite', 'unit', 'subpremise'],
    example: 'Suite 100',
  },
  {
    id: 'city',
    templateLabel: 'City',
    prefixedLabel: 'City',
    aliases: ['city', 'town'],
    example: 'Billings',
  },
  {
    id: 'state',
    templateLabel: 'State',
    prefixedLabel: 'State',
    aliases: ['state', 'province', 'region'],
    example: 'MT',
  },
  {
    id: 'zip',
    templateLabel: 'Zip',
    prefixedLabel: 'Zip',
    aliases: ['zip', 'zipcode', 'postal', 'postalcode', 'postcode'],
    example: '59106',
  },
  {
    id: 'country',
    templateLabel: 'Country',
    prefixedLabel: 'Country',
    aliases: ['country'],
    example: 'US',
  },
];

const LOCATION_FULL_ALIASES = ['address', 'fulladdress', 'mailingaddress'];

const cleanString = (value) => String(value || '').trim();

export const normalizeLocationHeaderToken = (value) => (
  String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '')
);

const stripKeyHint = (value) => String(value || '').replace(/\s*\([^)]*\)\s*\d*\s*$/, '').trim();

/**
 * Whether a schema field is a location value.
 *
 * @param {object|null|undefined} field
 * @returns {boolean}
 */
export function isLocationImportField(field) {
  return String(field?.type || '').trim().toLowerCase() === LOCATION_FIELD_TYPE;
}

/**
 * @param {string} fieldKey
 * @param {string} partId
 * @returns {string}
 */
export function buildLocationPartTarget(fieldKey, partId) {
  return `${String(fieldKey || '').trim()}${LOCATION_PART_SEPARATOR}${String(partId || '').trim()}`;
}

/**
 * @param {string} target
 * @returns {{ fieldKey: string, part: string }|null}
 */
export function parseLocationPartTarget(target) {
  const value = String(target || '');
  const separatorIndex = value.indexOf(LOCATION_PART_SEPARATOR);
  if (separatorIndex <= 0) return null;

  const fieldKey = value.slice(0, separatorIndex).trim();
  const part = value.slice(separatorIndex + LOCATION_PART_SEPARATOR.length).trim();
  if (!fieldKey || !part) return null;

  return { fieldKey, part };
}

/**
 * @param {Array<object>} fields
 * @returns {Array<object>}
 */
export function listLocationImportFields(fields = []) {
  return (Array.isArray(fields) ? fields : []).filter((field) => (
    field?.supported !== false && isLocationImportField(field)
  ));
}

const countLocationLabels = (locationFields) => {
  const counts = new Map();
  locationFields.forEach((field) => {
    const token = normalizeLocationHeaderToken(field?.label || field?.key);
    if (!token) return;
    counts.set(token, (counts.get(token) || 0) + 1);
  });
  return counts;
};

/**
 * Whether location part columns should be prefixed with the field label.
 * Always prefix when a type has more than one location field, or when an
 * unprefixed part name would collide with another importable field.
 *
 * @param {Array<object>} fields
 * @returns {boolean}
 */
export function shouldPrefixLocationPartHeaders(fields = []) {
  const locationFields = listLocationImportFields(fields);
  if (locationFields.length > 1) return true;
  if (locationFields.length === 0) return false;

  const otherTokens = new Set(
    (Array.isArray(fields) ? fields : [])
      .filter((field) => field?.supported !== false && !isLocationImportField(field))
      .flatMap((field) => [
        normalizeLocationHeaderToken(field?.label),
        normalizeLocationHeaderToken(field?.key),
      ])
      .filter(Boolean),
  );

  return LOCATION_ADDRESS_PARTS.some((part) => (
    otherTokens.has(normalizeLocationHeaderToken(part.templateLabel))
  ));
}

/**
 * @param {object} field
 * @param {object} part
 * @param {{ prefix?: boolean, disambiguateKey?: boolean }} [options]
 * @returns {string}
 */
export function buildLocationPartHeader(field, part, { prefix = false, disambiguateKey = false } = {}) {
  const fieldLabel = cleanString(field?.label || field?.key) || 'Address';
  const partLabel = prefix
    ? cleanString(part?.prefixedLabel)
    : cleanString(part?.templateLabel);
  const base = prefix ? `${fieldLabel} ${partLabel}` : partLabel;
  if (!disambiguateKey) return base;
  const fieldKey = cleanString(field?.key);
  return fieldKey ? `${base} (${fieldKey})` : base;
}

/**
 * Expands a location field into CSV columns for the address parts.
 *
 * @param {object} field
 * @param {{ prefix?: boolean, disambiguateKey?: boolean }} [options]
 * @returns {Array<{ header: string, field: object, locationPart: string, example: string }>}
 */
export function expandLocationFieldColumns(field, { prefix = false, disambiguateKey = false } = {}) {
  return LOCATION_ADDRESS_PARTS.map((part) => ({
    header: buildLocationPartHeader(field, part, { prefix, disambiguateKey }),
    field,
    locationPart: part.id,
    example: part.example || '',
  }));
}

/**
 * Resolves a CSV header to a location-part mapping target, or null.
 *
 * Unprefixed aliases (City, Address Line 1, …) only attach when the type has
 * exactly one location field. Prefixed headers always map.
 *
 * @param {string} header
 * @param {Array<object>} fields
 * @returns {string|null}
 */
export function resolveLocationPartMapping(header, fields = []) {
  const locationFields = listLocationImportFields(fields);
  if (!locationFields.length) return null;

  const normalizedHeader = normalizeLocationHeaderToken(header);
  const simplifiedHeader = normalizeLocationHeaderToken(stripKeyHint(header));
  if (!normalizedHeader && !simplifiedHeader) return null;

  const prefix = shouldPrefixLocationPartHeaders(fields);
  const labelCounts = countLocationLabels(locationFields);
  const prefixedTokens = new Map();

  locationFields.forEach((field) => {
    const fieldKey = cleanString(field?.key);
    if (!fieldKey) return;

    const labelToken = normalizeLocationHeaderToken(field?.label || fieldKey);
    const disambiguateKey = (labelCounts.get(labelToken) || 0) > 1;

    LOCATION_ADDRESS_PARTS.forEach((part) => {
      const target = buildLocationPartTarget(fieldKey, part.id);
      const candidates = [
        buildLocationPartHeader(field, part, { prefix, disambiguateKey }),
        `${field?.label || fieldKey} ${part.prefixedLabel}`,
        `${field?.label || fieldKey} ${part.templateLabel}`,
        `${fieldKey} ${part.prefixedLabel}`,
        `${fieldKey} ${part.templateLabel}`,
        `${fieldKey} ${part.id}`,
      ];
      candidates.forEach((candidate) => {
        const token = normalizeLocationHeaderToken(candidate);
        if (token) prefixedTokens.set(token, target);
      });
    });

    const fullTarget = buildLocationPartTarget(fieldKey, LOCATION_FULL_PART_ID);
    [
      field?.label,
      fieldKey,
    ].forEach((candidate) => {
      const token = normalizeLocationHeaderToken(candidate);
      if (token) prefixedTokens.set(token, fullTarget);
    });
  });

  if (prefixedTokens.has(normalizedHeader)) return prefixedTokens.get(normalizedHeader);
  if (simplifiedHeader && prefixedTokens.has(simplifiedHeader)) {
    return prefixedTokens.get(simplifiedHeader);
  }

  if (locationFields.length !== 1) return null;

  const fieldKey = cleanString(locationFields[0]?.key);
  if (!fieldKey) return null;

  const unprefixedTokens = new Map();
  LOCATION_ADDRESS_PARTS.forEach((part) => {
    const target = buildLocationPartTarget(fieldKey, part.id);
    unprefixedTokens.set(normalizeLocationHeaderToken(part.templateLabel), target);
    unprefixedTokens.set(normalizeLocationHeaderToken(part.prefixedLabel), target);
    (part.aliases || []).forEach((alias) => {
      const token = normalizeLocationHeaderToken(alias);
      if (token) unprefixedTokens.set(token, target);
    });
  });

  const fullTarget = buildLocationPartTarget(fieldKey, LOCATION_FULL_PART_ID);
  LOCATION_FULL_ALIASES.forEach((alias) => {
    const token = normalizeLocationHeaderToken(alias);
    if (token) unprefixedTokens.set(token, fullTarget);
  });

  if (unprefixedTokens.has(normalizedHeader)) return unprefixedTokens.get(normalizedHeader);
  if (simplifiedHeader && unprefixedTokens.has(simplifiedHeader)) {
    return unprefixedTokens.get(simplifiedHeader);
  }

  return null;
}

const composeImportedLocationTitleFull = ({
  route = '',
  subpremise = '',
  city = '',
  state = '',
  zip = '',
  country = '',
} = {}) => {
  const street = [route, subpremise].filter(Boolean).join(', ');
  const region = [
    city,
    [state, zip].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');
  return [street, region, country].filter(Boolean).join(', ');
};

/**
 * Builds a mailing-address location object from CSV parts. Does not geocode.
 *
 * @param {object} parts
 * @returns {object|null}
 */
export function assembleImportedLocation(parts = {}) {
  const line1 = cleanString(parts.line1);
  const line2 = cleanString(parts.line2);
  const city = cleanString(parts.city);
  const state = cleanString(parts.state);
  const zip = cleanString(parts.zip);
  const country = cleanString(parts.country);
  const full = cleanString(parts.full);

  if (!line1 && !line2 && !city && !state && !zip && !country && !full) {
    return null;
  }

  const route = line1 || full;
  const subpremise = line2;
  const titleFull = composeImportedLocationTitleFull({
    route,
    subpremise,
    city,
    state,
    zip,
    country,
  }) || full;
  const title = city || titleFull;

  return {
    streetNumber: '',
    route: route || '',
    subpremise: subpremise || '',
    city,
    county: '',
    state,
    country,
    zip,
    latitude: null,
    longitude: null,
    placeId: '',
    title: title || '',
    titleFull: titleFull || '',
  };
}

/**
 * Reads one address part from a stored location for CSV export.
 *
 * @param {object|null|undefined} location
 * @param {string} partId
 * @returns {string}
 */
export function readLocationPartValue(location, partId) {
  if (!location || typeof location !== 'object') return '';

  if (partId === 'line1') {
    return [cleanString(location.streetNumber), cleanString(location.route)]
      .filter(Boolean)
      .join(' ');
  }

  if (partId === 'line2') return cleanString(location.subpremise);
  if (partId === 'city') return cleanString(location.city);
  if (partId === 'state') return cleanString(location.state);
  if (partId === 'zip') return cleanString(location.zip);
  if (partId === 'country') return cleanString(location.country);

  return '';
}
